import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';

function ArticleCard({ article }: { article: typeof articles[0] }) {
  const category = categories.find((c) => c.slug === article.category);
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300 flex-shrink-0 w-[280px] md:w-[300px]"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={`五一吃瓜网 - ${article.title}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {article.exclusive && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-foreground-950 text-background-50 text-xs font-medium rounded-lg whitespace-nowrap">
            独家
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500 flex-wrap">
          {category && (
            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
              {category.name}
            </span>
          )}
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}

function FeaturedCard({ article, variant }: { article: typeof articles[0]; variant: 'left' | 'center' | 'right' }) {
  const category = categories.find((c) => c.slug === article.category);
  const isLeft = variant === 'left';

  return (
    <Link
      to={`/article/${article.id}`}
      className={`group bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300 flex flex-col ${
        isLeft ? 'lg:col-span-5' : 'lg:col-span-3'
      }`}
    >
      {isLeft ? (
        <>
          <div className="relative h-52 overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`五一吃瓜网 - ${article.title}`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            {article.exclusive && (
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-foreground-950 text-background-50 text-xs font-medium rounded-lg whitespace-nowrap">
                独家
              </span>
            )}
          </div>
          <div className="p-5 flex-1 flex flex-col justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                {article.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
            </div>
            <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500 flex-wrap">
              {category && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                  {category.name}
                </span>
              )}
              <span>{article.publishDate}</span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="p-5 flex-1 flex flex-col justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                {article.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
            </div>
            <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500 flex-wrap">
              {category && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                  {category.name}
                </span>
              )}
              <span>{article.publishDate}</span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
              </span>
            </div>
          </div>
          <div className="relative h-44 overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`五一吃瓜网 - ${article.title}`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          </div>
        </>
      )}
    </Link>
  );
}

export default function Home() {
  const featured = articles.filter((a) => a.featured).slice(0, 3);
  const latest = articles.slice(0, 8);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com/' },
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '五一吃瓜网',
    url: 'https://www.51chigua.com',
    description: '五一吃瓜网是您的吃瓜第一站，支持在线观看各类网红明星黑料爆料。独家内容每日更新，吃瓜从未如此直接。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.51chigua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '五一吃瓜网',
    url: 'https://www.51chigua.com',
    description: '五一吃瓜网 - 吃瓜第一站，专注网红明星黑料爆料的实时追踪平台。',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dramatic%20warm%20toned%20abstract%20background%20with%20soft%20gradients%20of%20amber%20and%20coral%2C%20modern%20editorial%20design%20with%20floating%20geometric%20shapes%2C%20news%20media%20aesthetic%2C%20clean%20and%20minimal%20composition%2C%20high%20contrast%20dark%20and%20warm%20accents%2C%20no%20people%2C%20artistic%20abstract%20photography&width=1800&height=1000&seq=hero-main-2026-01&orientation=landscape"
            alt="五一吃瓜网 - 吃瓜第一站"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-tight">
              五一吃瓜网
            </h1>
            <p className="text-xl md:text-2xl font-heading font-bold text-white/90 mt-3">
              吃瓜第一站
            </p>
            <p className="mt-6 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed">
              五一吃瓜网是您的吃瓜第一站，支持在线观看各类网红明星黑料爆料。本站提供五一吃瓜网app下载入口，方便您随时追最新塌房现场和热门事件，独家内容每日更新，吃瓜从未如此直接。
            </p>
            <a
              href="#app-download"
              className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              立即追更
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-line"></i>
              </span>
            </a>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-10 md:mb-14">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              热门爆料 · 实时追踪
            </h2>
            <p className="text-sm text-foreground-500 md:pt-2">
              第一时间掌握最新瓜情，不错过每一个塌房现场
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-11 gap-4 md:gap-5">
            {featured.map((article, index) => (
              <FeaturedCard
                key={article.id}
                article={article}
                variant={index === 0 ? 'left' : index === 1 ? 'center' : 'right'}
              />
            ))}
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 md:mb-14 text-center">
            最新吃瓜
          </h2>

          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4">
            {latest.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/category/remen-zhuizong"
              className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-300 whitespace-nowrap"
            >
              查看全部文章
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-5 py-2.5 bg-background-50 border border-background-200 rounded-full text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-300 whitespace-nowrap"
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {articles.slice(0, 4).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`五一吃瓜网 - ${article.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between mt-3 text-xs text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-eye-line"></i>
                        </span>
                        {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="app-download" className="py-14 md:py-20 px-4 md:px-6 max-w-4xl mx-auto text-center">
          <div className="relative rounded-3xl overflow-hidden mb-12 md:mb-16">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20a%20news%20app%20interface%20with%20warm%20amber%20and%20coral%20accent%20colors%2C%20placed%20on%20a%20clean%20minimal%20wooden%20desk%2C%20soft%20natural%20lighting%20from%20window%2C%20cozy%20modern%20workspace%20aesthetic%2C%20film%20grain%20texture%2C%20editorial%20product%20photography&width=1400&height=700&seq=app-cta-banner-2026&orientation=landscape"
              alt="五一吃瓜网APP"
              className="w-full h-auto rounded-3xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-3xl"></div>
          </div>

          <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-relaxed">
            随时随地
            <span className="text-foreground-400 italic font-light mx-2">吃瓜自由</span>
          </p>
          <p className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-relaxed mt-1">
            下载五一吃瓜网APP
          </p>

          <a
            href="#"
            className="mt-10 inline-flex items-center gap-2 px-10 py-4 bg-foreground-950 text-background-50 rounded-full text-base font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap"
            rel="nofollow"
          >
            下载五一吃瓜网APP
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-download-line"></i>
            </span>
          </a>
        </section>
      </main>

      <Footer />
    </>
  );
}